// external
import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

// components
import ProgressTracker from "components/ProgressTracker";
import StepNavigation from "components/StepNavigation";
import CustomTextInput from "components/partials/custom-text-input/custom-text-input";
import CustomSelect from "components/partials/custom-select/custom-select";
import FaoBuilderHeader from "./fao-layout/fao-builder-header";
import RadioGroup from "components/partials/RadioGroup";
import Loader from "components/partials/loader/loader";

// api
import apiRoot from "api/apiRoot";

// state managing/security
import { ClientContext } from "context/ClientContext";
import { AuthContext } from "context/AuthContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";

// css/styling
import { STYLES, mergeStyles } from "./constants/styles.constants";

const selectStyle = {
  control: mergeStyles(STYLES.select.control, (provided) => ({
    width: "100%",
  })),
  option: STYLES.select.option,
  menuPortal: STYLES.select.menuPortal,
};
function FaoMetadata() {
  const navigate = useNavigate();
  const [modelName, setModelName] = useState("");
  const [entryTerm, setEntryTerm] = useState(null);
  const [studentType, setStudentType] = useState("FY"); // Default to FY
  const { currentClientData } = useContext(ClientContext);
  const { userName } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const { modelConfig, setModelConfig } = useContext(FaoBuilderContext);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const [radioValue, setRadioValue] = useState([
    {
      label: "First Year",
      radioValue: "FY",
      ischecked: true,
    },
    {
      label: "Transfer",
      radioValue: "Transfer",
      ischecked: false,
    },
  ]);

  const entryTermOptions = [
    { value: "Fall 2024", label: "Fall 2024" },
    { value: "Spring 2025", label: "Spring 2025" },
    { value: "Fall 2025", label: "Fall 2025" },
  ];

  const handleStudentTypeChange = (value) => {
    setStudentType(value);
    const updatedRadioValue = radioValue.map((radio) => {
      // If the radio value matches the selected value, set isChecked to true, otherwise false
      return {
        ...radio,
        isChecked: radio.radioValue === value,
      };
    });
    setRadioValue(updatedRadioValue);
  };
  useEffect(() => {
    setIsSaved(false);
  }, [modelName, entryTerm, studentType]);
  useEffect(() => {
    const handleLoad = async () => {
      if (modelConfig?.id && currentClientData?.schoolKey) {
        try {
          // Fetch existing metadata using the correct endpoint structure
          const metadata = modelConfig;

          if (
            metadata.versionName &&
            metadata.entryTerm &&
            metadata.studentType
          ) {
            toast.success("Policy metadata found");
            // Set form state with existing data
            setModelName(metadata.versionName);
            setStudentType(metadata.studentType);

            // Update radio buttons based on retrieved student type
            const updatedRadioValue = radioValue.map((radio) => ({
              ...radio,
              isChecked: radio.radioValue === metadata.studentType,
            }));
            setRadioValue(updatedRadioValue);

            // Set entry term directly from metadata if it exists in our options
            const selectedTerm = entryTermOptions.find(
              (option) => option.value === metadata.entryTerm
            );

            if (selectedTerm) {
              setEntryTerm(selectedTerm);
            } else {
              // If no matching term found, set to first option as fallback
              setEntryTerm(entryTermOptions[0]);
            }
          }
        } catch (error) {
          console.error("Error loading metadata:", error);
          toast.error("Failed to load metadata");
        }
      }
      setIsLoading(false);
    };
    handleLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Keep empty dependency array to ensure it only runs once

  const postMetadata = async () => {
    if (!modelName || !entryTerm || !studentType) {
      toast.error("Please populate all fields");
      return;
    }
    setIsSaving(true);
    try {
      const toastId = toast.loading("Saving FAO Metadata...");

      // Create update DTO with metadata fields
      const updateDto = {
        ...modelConfig,
        versionName: modelName,
        entryTerm: entryTerm.value,
        studentType: studentType,
        authorName: userName,
      };

      // Update model with PATCH request
      const response = await apiRoot.patch(
        `/api/fao-model/${modelConfig.id}`,
        updateDto
      );
      setModelConfig(response.data);

      // Update toast to success
      toast.update(toastId, {
        render: "SUCCESS - FAO Metadata Saved",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving metadata:", error);
      toast.error(error.message || "Failed to save metadata");
    }
    setIsSaving(false);
    setIsSaved(true);
  };

  return (
    <div className={`flex h-full w-full flex-col justify-between`}>
      <div className="mx-auto flex w-full flex-col">
        <ToastContainer />
        <FaoBuilderHeader
          modelName={modelConfig.versionName}
          modelId={modelConfig.id}
        />
        <ProgressTracker />
        {isLoading ? (
          <Loader />
        ) : (
          <div className="space-y-8">
            {/* Model Name Input */}
            <div className="flex flex-col gap-2">
              <CustomTextInput
                id="modelName"
                label="Model Name"
                customClass={STYLES.label}
                type="text"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                inputcustomclass="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-01"
                inputWrapperCustomClass="w-full"
                placeholder="Enter Model Name ..."
                required
                labelInTop
              />
            </div>

            {/* Entry Term Input */}
            <div className="flex flex-col gap-4">
              <CustomSelect
                label="Entry Term"
                customClass={`${STYLES.label} mb-2`}
                id="entryTerm"
                isSearchable={false}
                options={entryTermOptions}
                value={entryTerm}
                onChange={(selected) => setEntryTerm(selected)}
                required={true}
                labelInTop={true}
                placeholder="Select Entry Term Year ..."
                extraStyles={() => selectStyle}
              />
            </div>

            {/* Student Type Radio Buttons*/}
            <div className="flex flex-col gap-2">
              <legend className={STYLES.label}>Student Type</legend>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <RadioGroup
                    htmlFor="student-type-radio"
                    name="studentType"
                    value={radioValue[0]}
                    options={radioValue}
                    onCheckboxClick={handleStudentTypeChange}
                  />
                </div>
              </div>
            </div>

            {/* Author Field */}
            <div className="flex flex-col gap-2">
              <label htmlFor="author" className={STYLES.label}>
                Author
              </label>
              <input
                id="author"
                type="text"
                value={userName}
                className="w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-600"
                readOnly
              />
            </div>
          </div>
        )}

        <StepNavigation
          onClickBack={() => navigate("create")}
          onClickNext={() =>
            navigate(`../fao-builder/${modelConfig.id}/import`)
          }
          onClickSave={postMetadata}
          isSaving={isSaving}
          isSaved={isSaved}
          disabledNext={!modelName || !entryTerm || !isSaved}
        />
      </div>
    </div>
  );
}

export default FaoMetadata;
